// Sino da TopBar + central de notificações.
//
// Out/26. Fiel ao design do Cetto Copiloto (Cetto Copiloto.dc.html):
// botão 34×34, badge verde, popover de 384px com vidro escuro, avisos
// agrupados por dia, ponto verde nos não lidos, ícone por tipo.
//
// O estado e o socket moram em services/notificacoes — este componente só
// assina. Ver lá o porquê: a TopBar é remontada a cada tela, e um socket
// aqui dentro reconectaria a cada clique no menu.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, Popover } from '@mui/material';
import {
  FiActivity, FiAlertTriangle, FiBell, FiCalendar, FiCheckCircle, FiClipboard, FiClock,
  FiRefreshCw, FiUserPlus, FiXCircle,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import Faisca from './copiloto/Faisca';
import { format, isToday, isYesterday } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  assinar, carregar, marcarLida, marcarTodas,
} from '../services/notificacoes';

const C = {
  bg2: '#0E1525',
  bg3: '#131C30',
  bdr: 'rgba(255,255,255,0.07)',
  bdr2: 'rgba(255,255,255,0.10)',
  text: '#DDE4F0',
  text2: '#7A8799',
  text3: '#3E4D60',
  text4: '#56657A',
  green: '#00D67C',
  greenHover: '#00f090',
  greenTint: 'rgba(0,214,124,0.10)',
  greenBdr: 'rgba(0,214,124,0.28)',
};
const FONT = "'Syne', sans-serif";
const FONT_NUM = "'Outfit', sans-serif";

// Ícone e cor por tipo — a mesma tabela do design (NTYPE).
const VERDE = { cor: '#00D67C', tint: 'rgba(0,214,124,0.10)', bdr: 'rgba(0,214,124,0.22)' };
const AMBAR = { cor: '#FFAA00', tint: 'rgba(255,170,0,0.12)', bdr: 'rgba(255,170,0,0.25)' };
const VERMELHO = { cor: '#FF4560', tint: 'rgba(255,69,96,0.12)', bdr: 'rgba(255,69,96,0.25)' };
const AZUL = { cor: '#5BA4F5', tint: 'rgba(91,164,245,0.10)', bdr: 'rgba(91,164,245,0.22)' };
const LILAS = { cor: '#9184d9', tint: 'rgba(145,132,217,0.12)', bdr: 'rgba(145,132,217,0.28)' };

function aparencia(n) {
  switch (n.type) {
    case 'schedule_created': return { Icone: FiCalendar, ...VERDE };
    case 'dispatch_success': return { Icone: FiCheckCircle, ...VERDE };
    case 'dispatch_partial': return { Icone: FiAlertTriangle, ...AMBAR };
    case 'dispatch_failed': return { Icone: FiXCircle, ...VERMELHO };
    case 'contact_assigned': return { Icone: FiUserPlus, ...AZUL };
    // Fase 4 do Copiloto: a faísca dele, no lilás dele (design, NTYPE).
    case 'copilot_action': return { Icone: Faisca, ...LILAS };
    case 'health_alert': {
      // O mesmo tipo carrega a queda e a volta ao normal: a cor vem do
      // nível, senão "qualidade normalizada" chegaria pintada de alarme.
      const nivel = n.meta?.nivel;
      if (nivel === 'ok') return { Icone: FiActivity, ...VERDE };
      if (nivel === 'critico') return { Icone: FiActivity, ...VERMELHO };
      return { Icone: FiActivity, ...AMBAR };
    }
    // Agenda, fase 2 (out/26) — ver backend services/agenda_avisos.
    case 'agenda_atribuida': return { Icone: FiCalendar, ...AZUL };
    case 'agenda_em_breve': return { Icone: FiClock, ...VERDE };
    case 'agenda_atrasada': return { Icone: FiAlertTriangle, ...AMBAR };
    case 'agenda_pendente': return { Icone: FiClock, ...AMBAR };
    case 'agenda_cliente':
      return n.meta?.resposta === 'confirmou'
        ? { Icone: FiCheckCircle, ...VERDE }
        : { Icone: FiRefreshCw, ...AMBAR };
    case 'agenda_lembrete_falhou': return { Icone: FiXCircle, ...VERMELHO };
    // Formulários PR 2 (out/26): o cliente respondeu um formulário.
    case 'formulario_resposta': return { Icone: FiClipboard, ...AZUL };
    default: return { Icone: FiBell, ...AZUL };
  }
}

// "agora", "12 min", "2 h", "ontem, 17:42", "05/10, 09:00" — como no design.
function quando(iso, agora) {
  if (!iso) return '';
  const d = new Date(iso);
  const minutos = Math.floor((agora - d) / 60000);
  if (minutos < 1) return 'agora';
  if (minutos < 60) return `${minutos} min`;
  if (isToday(d)) return `${Math.floor(minutos / 60)} h`;
  if (isYesterday(d)) return `ontem, ${format(d, 'HH:mm')}`;
  return format(d, 'dd/MM, HH:mm');
}

function rotuloDoDia(iso) {
  const d = new Date(iso);
  if (isToday(d)) return 'Hoje';
  if (isYesterday(d)) return 'Ontem';
  return format(d, "EEE, d 'de' MMM", { locale: ptBR });
}

export default function CentralNotificacoes() {
  const [estado, setEstado] = useState(null);
  const [aberto, setAberto] = useState(false);
  const [agora, setAgora] = useState(() => Date.now());
  const ancora = useRef(null);
  const navigate = useNavigate();

  useEffect(() => assinar(setEstado), []);

  // A lista carrega ao ABRIR a central, não ao montar a TopBar: o badge
  // não precisa dela (vem da contagem), e cada troca de tela não deve custar
  // uma consulta da lista.
  //
  // Uma vez por abertura, e não a cada mudança de estado: se o carregamento
  // falhasse, o estado "não carregado e não carregando" dispararia outra
  // tentativa, e outra — um laço contra a API. Depois de um erro, quem
  // tenta de novo é o botão, ou a próxima abertura.
  useEffect(() => {
    if (aberto && estado && !estado.carregado && !estado.carregando) carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto]);

  // Tempo relativo vivo enquanto a central está aberta ("2 min" vira "3").
  useEffect(() => {
    if (!aberto) return undefined;
    setAgora(Date.now());
    const id = setInterval(() => setAgora(Date.now()), 30000);
    return () => clearInterval(id);
  }, [aberto]);

  const grupos = useMemo(() => {
    const porDia = [];
    (estado?.itens || []).forEach((n) => {
      const dia = rotuloDoDia(n.created_at);
      const ultimo = porDia[porDia.length - 1];
      if (ultimo && ultimo.dia === dia) ultimo.itens.push(n);
      else porDia.push({ dia, itens: [n] });
    });
    return porDia;
  }, [estado?.itens]);

  const naoLidas = estado?.naoLidas || 0;
  const badge = naoLidas > 99 ? '99+' : String(naoLidas);

  const abrirAviso = (n) => {
    marcarLida(n.id);
    setAberto(false);
    if (n.link) navigate(n.link);
  };

  return (
    <>
      <Box
        component="button"
        type="button"
        ref={ancora}
        onClick={() => setAberto((v) => !v)}
        aria-label={naoLidas ? `Notificações, ${naoLidas} não lidas` : 'Notificações'}
        aria-haspopup="dialog"
        aria-expanded={aberto}
        sx={{
          position: 'relative', width: 34, height: 34, flexShrink: 0, p: 0,
          borderRadius: '7px',
          border: `1px solid ${aberto ? C.greenBdr : C.bdr}`,
          bgcolor: aberto ? C.greenTint : 'transparent',
          color: aberto ? C.green : C.text2,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', transition: 'all .15s ease',
          '&:hover': { color: C.text, bgcolor: 'rgba(255,255,255,0.04)' },
          '&:focus-visible': { outline: `2px solid ${C.green}`, outlineOffset: 2 },
        }}
      >
        <FiBell size={15} />
        {naoLidas > 0 && (
          <Box component="span" sx={{
            position: 'absolute', top: -6, right: -6,
            minWidth: 18, height: 18, px: '4px', borderRadius: '9px',
            bgcolor: C.green, color: '#060d14',
            fontFamily: FONT_NUM, fontSize: '0.6rem', fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: `2px solid ${C.bg2}`, lineHeight: 1,
          }}>
            {badge}
          </Box>
        )}
      </Box>

      <Popover
        open={aberto}
        anchorEl={ancora.current}
        onClose={() => setAberto(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            role: 'dialog',
            'aria-label': 'Notificações',
            sx: {
              mt: 1.2,
              width: { xs: 'calc(100vw - 24px)', sm: 384 },
              maxWidth: 384,
              bgcolor: 'rgba(14,21,37,0.94)',
              backdropFilter: 'blur(20px) saturate(140%)',
              border: `1px solid ${C.bdr2}`,
              borderRadius: '12px',
              boxShadow: '0 18px 48px rgba(0,0,0,0.45), 0 2px 8px rgba(0,0,0,0.25)',
              overflow: 'hidden',
              fontFamily: FONT,
              backgroundImage: 'none',
            },
          },
        }}
      >
        {/* Cabeçalho */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: '14px 16px 10px' }}>
          <Box component="span" sx={{ fontSize: '0.82rem', fontWeight: 700, color: C.text }}>
            Notificações
          </Box>
          <Box component="span" sx={{ fontFamily: FONT_NUM, fontSize: '0.66rem', color: C.text3 }}>
            {naoLidas ? `${naoLidas} não lida${naoLidas > 1 ? 's' : ''}` : 'tudo lido'}
          </Box>
          <Box
            component="button"
            type="button"
            onClick={marcarTodas}
            disabled={!naoLidas}
            sx={{
              ml: 'auto', background: 'none', border: 'none', p: '2px 0',
              fontFamily: FONT, fontSize: '0.68rem', fontWeight: 600,
              color: naoLidas ? C.green : C.text3,
              cursor: naoLidas ? 'pointer' : 'default',
              '&:hover:not(:disabled)': { color: C.greenHover },
            }}
          >
            Marcar todas como lidas
          </Box>
        </Box>

        {/* Lista */}
        <Box sx={{
          maxHeight: 440, overflowY: 'auto', pb: '6px',
          scrollbarWidth: 'thin', scrollbarColor: '#1A2540 transparent',
        }}>
          {!estado?.carregado && estado?.carregando && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={18} sx={{ color: C.text3 }} />
            </Box>
          )}

          {estado?.erro && !estado?.itens.length && (
            <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
              <Box sx={{ fontSize: '0.75rem', color: C.text2, mb: 1 }}>
                Não foi possível carregar os avisos.
              </Box>
              <Box component="button" type="button" onClick={() => carregar()} sx={linkSx}>
                Tentar de novo
              </Box>
            </Box>
          )}

          {estado?.carregado && !estado.itens.length && !estado.erro && (
            <Box sx={{ px: 3, py: 4, textAlign: 'center' }}>
              <Box sx={{ color: C.text3, display: 'flex', justifyContent: 'center', mb: 1.2 }}>
                <FiBell size={20} />
              </Box>
              <Box sx={{ fontSize: '0.78rem', fontWeight: 700, color: C.text2 }}>
                Nada por aqui ainda
              </Box>
              <Box sx={{ fontSize: '0.7rem', color: C.text3, mt: 0.6, lineHeight: 1.5 }}>
                Os disparos agendados, a saúde dos chips e os contatos atribuídos
                a você aparecem aqui assim que acontecerem.
              </Box>
            </Box>
          )}

          {grupos.map((g) => (
            <Box key={g.dia}>
              <Box sx={{
                fontSize: '0.58rem', fontWeight: 700, letterSpacing: '0.16em',
                color: C.text3, textTransform: 'uppercase', p: '10px 16px 4px',
              }}>
                {g.dia}
              </Box>
              {g.itens.map((n) => {
                const { Icone, cor, tint, bdr } = aparencia(n);
                const lida = n.read;
                return (
                  <Box
                    key={n.id}
                    component="button"
                    type="button"
                    onClick={() => abrirAviso(n)}
                    sx={{
                      display: 'flex', gap: 1.5, width: '100%', textAlign: 'left',
                      p: '10px 16px 10px 18px', border: 'none', cursor: 'pointer',
                      bgcolor: lida ? 'transparent' : 'rgba(255,255,255,0.02)',
                      position: 'relative', fontFamily: FONT,
                      transition: 'background .15s',
                      '&:hover': { bgcolor: C.bg3 },
                      '&:focus-visible': { outline: `2px solid ${C.green}`, outlineOffset: -2 },
                    }}
                  >
                    {!lida && (
                      <Box aria-hidden sx={{
                        position: 'absolute', left: 7, top: 22, width: 5, height: 5,
                        borderRadius: '50%', bgcolor: C.green,
                        boxShadow: '0 0 4px rgba(0,214,124,0.22)',
                      }} />
                    )}
                    <Box sx={{
                      width: 30, height: 30, borderRadius: '8px', flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      bgcolor: tint, color: cor, border: `1px solid ${bdr}`,
                    }}>
                      <Icone size={14} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                        <Box component="span" sx={{
                          flex: 1, fontSize: '0.76rem', lineHeight: 1.35,
                          fontWeight: lida ? 600 : 700,
                          color: lida ? C.text2 : C.text,
                        }}>
                          {n.title}
                        </Box>
                        <Box component="span" sx={{
                          fontFamily: FONT_NUM, fontSize: '0.62rem', color: C.text3,
                          whiteSpace: 'nowrap',
                        }}>
                          {quando(n.created_at, agora)}
                        </Box>
                      </Box>
                      {n.body && (
                        <Box sx={{
                          fontSize: '0.7rem', lineHeight: 1.5, mt: '2px',
                          color: lida ? C.text4 : C.text2, textWrap: 'pretty',
                        }}>
                          {n.body}
                        </Box>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ))}

          {estado?.proximoCursor && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.2 }}>
              <Box
                component="button"
                type="button"
                onClick={() => carregar({ mais: true })}
                disabled={estado.carregando}
                sx={linkSx}
              >
                {estado.carregando ? 'Carregando…' : 'Ver avisos anteriores'}
              </Box>
            </Box>
          )}
        </Box>
      </Popover>
    </>
  );
}

const linkSx = {
  background: 'none', border: 'none', p: 0, cursor: 'pointer',
  fontFamily: FONT, fontSize: '0.7rem', fontWeight: 600, color: C.green,
  '&:hover': { color: C.greenHover },
  '&:disabled': { color: C.text3, cursor: 'default' },
};
